<template>
  <div class="open-platform-docs">
    <header class="docs-header">
      <div class="brand" aria-label="Solar Energy 开放平台">
        <span class="brand-mark" aria-hidden="true">
          <svg viewBox="0 0 40 40" role="img">
            <circle cx="20" cy="20" r="7"></circle>
            <path d="M20 3v6M20 31v6M3 20h6M31 20h6M8 8l4.2 4.2M27.8 27.8 32 32M32 8l-4.2 4.2M12.2 27.8 8 32"></path>
          </svg>
        </span>
        <span class="brand-name">Solar Energy</span>
        <span class="brand-divider"></span>
        <span class="brand-product">开放平台</span>
      </div>

      <div class="header-actions">
        <router-link class="console-link" to="/">
          管理控制台
          <svg viewBox="0 0 20 20" aria-hidden="true">
            <path d="M7.5 4.5 13 10l-5.5 5.5"></path>
          </svg>
        </router-link>
      </div>
    </header>

    <div class="docs-layout">
      <aside class="docs-sidebar" aria-label="文档导航">
        <div class="sidebar-inner">
          <div class="nav-caption">开始使用</div>
          <nav class="docs-nav">
            <button
              type="button"
              :class="['nav-item', { active: activeSection === 'introduction' }]"
              @click="selectSection('introduction')"
            >
              <span class="nav-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24">
                  <path d="M4.5 5.5A2.5 2.5 0 0 1 7 3h12.5v15H7A2.5 2.5 0 0 0 4.5 20.5v-15Z"></path>
                  <path d="M4.5 20.5A2.5 2.5 0 0 1 7 18h12.5v3H7a2.5 2.5 0 0 1-2.5-2.5M8 7h8M8 11h6"></path>
                </svg>
              </span>
              <span>
                <strong>平台简介</strong>
                <small>了解能力与接入流程</small>
              </span>
              <svg class="nav-arrow" viewBox="0 0 20 20" aria-hidden="true">
                <path d="M7.5 4.5 13 10l-5.5 5.5"></path>
              </svg>
            </button>

            <button
              type="button"
              :class="['nav-item', { active: activeSection === 'security' }]"
              @click="selectSection('security')"
            >
              <span class="nav-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24">
                  <path d="M12 3 4.5 6v5.5c0 4.6 3.2 8.1 7.5 9.5 4.3-1.4 7.5-4.9 7.5-9.5V6L12 3Z"></path>
                  <path d="m8.5 12 2.2 2.2 4.8-5"></path>
                </svg>
              </span>
              <span>
                <strong>安全鉴权</strong>
                <small>OAuth 2.0 与访问令牌</small>
              </span>
              <svg class="nav-arrow" viewBox="0 0 20 20" aria-hidden="true">
                <path d="M7.5 4.5 13 10l-5.5 5.5"></path>
              </svg>
            </button>

            <button
              type="button"
              :class="['nav-item', { active: activeSection === 'idempotency' }]"
              @click="selectSection('idempotency')"
            >
              <span class="nav-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24">
                  <path d="M7.5 7.5A6.5 6.5 0 1 1 5.6 13M3.5 7.5h4v4"></path>
                  <path d="M12 8.5v4l2.7 1.7"></path>
                </svg>
              </span>
              <span>
                <strong>幂等请求</strong>
                <small>安全重试与幂等键</small>
              </span>
              <svg class="nav-arrow" viewBox="0 0 20 20" aria-hidden="true">
                <path d="M7.5 4.5 13 10l-5.5 5.5"></path>
              </svg>
            </button>

            <button
              type="button"
              :class="['nav-item', { active: activeSection === 'response' }]"
              @click="selectSection('response')"
            >
              <span class="nav-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24">
                  <path d="M5 4h14v16H5zM8 8h8M8 12h8M8 16h5"></path>
                  <path d="m15.5 15.5 1.5 1.5 3-3"></path>
                </svg>
              </span>
              <span>
                <strong>统一响应</strong>
                <small>响应结构与错误处理</small>
              </span>
              <svg class="nav-arrow" viewBox="0 0 20 20" aria-hidden="true">
                <path d="M7.5 4.5 13 10l-5.5 5.5"></path>
              </svg>
            </button>
          </nav>

          <div class="nav-caption api-nav-caption">接口文档</div>
          <nav class="api-catalog" aria-label="接口列表">
            <button
              type="button"
              :class="['nav-item', 'api-catalog-root', { active: activeSection === 'api' }]"
              :aria-expanded="String(apiCatalogExpanded)"
              @click="apiCatalogExpanded = !apiCatalogExpanded"
            >
              <span class="nav-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24">
                  <path d="m8 7-5 5 5 5M16 7l5 5-5 5M14 4l-4 16"></path>
                </svg>
              </span>
              <span>
                <strong>接口列表</strong>
                <small>{{ apiEndpointCount ? apiEndpointCount + ' 个开放接口' : '从 Springdoc 加载' }}</small>
              </span>
              <svg :class="['nav-arrow', 'catalog-arrow', { expanded: apiCatalogExpanded }]" viewBox="0 0 20 20" aria-hidden="true">
                <path d="M7.5 4.5 13 10l-5.5 5.5"></path>
              </svg>
            </button>

            <div v-if="apiCatalogExpanded" class="api-catalog-tree">
              <div v-if="apiLoading" class="catalog-state">正在加载接口列表...</div>
              <button v-else-if="apiLoadError" type="button" class="catalog-state catalog-retry" @click="loadApiDocument">
                加载失败，点击重试
              </button>
              <div v-else-if="!apiGroups.length" class="catalog-state">暂无开放接口</div>
              <template v-else>
                <div v-for="group in apiGroups" :key="group.name" class="api-group">
                  <button
                    type="button"
                    class="api-group-title"
                    :aria-expanded="String(isApiGroupExpanded(group.name))"
                    @click="toggleApiGroup(group.name)"
                  >
                    <span>{{ group.name }}</span>
                    <small>{{ group.endpoints.length }}</small>
                    <svg :class="{ expanded: isApiGroupExpanded(group.name) }" viewBox="0 0 20 20" aria-hidden="true">
                      <path d="M7.5 4.5 13 10l-5.5 5.5"></path>
                    </svg>
                  </button>
                  <div v-if="isApiGroupExpanded(group.name)" class="api-endpoint-list">
                    <button
                      v-for="endpoint in group.endpoints"
                      :key="group.name + ':' + endpoint.routeKey"
                      type="button"
                      :class="['api-endpoint-item', { active: activeEndpoint && activeEndpoint.routeKey === endpoint.routeKey }]"
                      @click="selectApi(endpoint)"
                    >
                      <span :class="['endpoint-method', endpoint.method.toLowerCase()]">{{ endpoint.method }}</span>
                      <span>{{ endpoint.summary }}</span>
                    </button>
                  </div>
                </div>
              </template>
            </div>
          </nav>

        </div>
      </aside>

      <main class="docs-main" ref="docsMain">
        <article v-if="activeSection === 'introduction'" class="docs-article intro-article">
          <div class="breadcrumb"><span>开放平台文档</span><i>/</i>平台简介</div>

          <section class="hero-section">
            <div class="hero-copy">
              <span class="eyebrow">SOLAR ENERGY OPEN PLATFORM</span>
              <h1>让光伏业务，连接得更简单</h1>
              <p>
                Solar Energy 开放平台为企业系统提供标准化的光伏电站开放能力，
                通过清晰、安全的 REST API，将电站管理流程接入您的业务应用。
              </p>
              <button type="button" class="primary-action" @click="selectSection('security')">
                开始接入
                <svg viewBox="0 0 20 20" aria-hidden="true"><path d="M4 10h11M11 6l4 4-4 4"></path></svg>
              </button>
            </div>
            <div class="hero-visual" aria-hidden="true">
              <div class="sun-orbit orbit-one"></div>
              <div class="sun-orbit orbit-two"></div>
              <div class="solar-sun">
                <svg viewBox="0 0 64 64">
                  <circle cx="32" cy="32" r="12"></circle>
                  <path d="M32 5v10M32 49v10M5 32h10M49 32h10M13 13l7 7M44 44l7 7M51 13l-7 7M20 44l-7 7"></path>
                </svg>
              </div>
            </div>
          </section>

          <section class="content-section">
            <div class="section-heading">
              <span class="section-index">01</span>
              <div>
                <h2>平台能力</h2>
                <p>围绕光伏电站业务，提供稳定、清晰且易于集成的开放能力。</p>
              </div>
            </div>

            <div class="capability-grid">
              <div class="capability-card">
                <span class="capability-icon amber" aria-hidden="true">
                  <svg viewBox="0 0 24 24"><path d="M4 19V9l8-5 8 5v10M8 19v-6h8v6M2.5 19h19"></path></svg>
                </span>
                <h3>电站全生命周期</h3>
                <p>支持电站新增、修改、详情查询与删除，便于外部业务系统统一编排电站数据。</p>
              </div>
              <div class="capability-card">
                <span class="capability-icon green" aria-hidden="true">
                  <svg viewBox="0 0 24 24"><path d="M12 3 4.5 6v5.5c0 4.6 3.2 8.1 7.5 9.5 4.3-1.4 7.5-4.9 7.5-9.5V6L12 3Z"></path><path d="M9 12h6M12 9v6"></path></svg>
                </span>
                <h3>租户数据隔离</h3>
                <p>访问令牌绑定 API 客户端与所属租户，开放接口只允许访问当前租户范围内的数据。</p>
              </div>
              <div class="capability-card">
                <span class="capability-icon blue" aria-hidden="true">
                  <svg viewBox="0 0 24 24"><path d="M4 6.5h16v11H4zM8 10h2M8 14h5M16 10h.1M16 14h.1"></path></svg>
                </span>
                <h3>标准化接口</h3>
                <p>遵循 REST 语义并使用 JSON 传输业务数据，成功响应包含统一的结果码与链路追踪 ID。</p>
              </div>
            </div>
          </section>

          <section class="content-section">
            <div class="section-heading">
              <span class="section-index">02</span>
              <div>
                <h2>接入前须知</h2>
                <p>在开始联调前了解这些通用约定，可以让鉴权、重试和异常处理更加顺畅。</p>
              </div>
            </div>

            <div class="intro-guide-grid">
              <button type="button" class="intro-guide-card" @click="selectSection('security')">
                <span class="guide-label">SECURITY</span>
                <h3>凭据只在服务端使用</h3>
                <p>通过 OAuth 2.0 客户端凭据模式获取访问令牌，并在业务请求中携带 Bearer Token；客户端密钥不得暴露在浏览器或移动端。</p>
                <span class="guide-link">
                  查看安全鉴权
                  <svg viewBox="0 0 20 20" aria-hidden="true"><path d="M4 10h11M11 6l4 4-4 4"></path></svg>
                </span>
              </button>
              <button type="button" class="intro-guide-card" @click="selectSection('idempotency')">
                <span class="guide-label">RETRY</span>
                <h3>写操作需要安全重试</h3>
                <p>新增电站等指定接口需要提供 <code>Idempotency-Key</code>；遇到超时或连接中断时，应保持业务参数不变并复用原键。</p>
                <span class="guide-link">
                  查看幂等请求
                  <svg viewBox="0 0 20 20" aria-hidden="true"><path d="M4 10h11M11 6l4 4-4 4"></path></svg>
                </span>
              </button>
              <button type="button" class="intro-guide-card" @click="selectSection('response')">
                <span class="guide-label">RESPONSE</span>
                <h3>按端点类型解析响应</h3>
                <p>Token 端点遵循 OAuth 响应格式，开放资源接口统一返回 <code>Result&lt;T&gt;</code>。</p>
                <span class="guide-link">
                  查看统一响应
                  <svg viewBox="0 0 20 20" aria-hidden="true"><path d="M4 10h11M11 6l4 4-4 4"></path></svg>
                </span>
              </button>
            </div>
          </section>

          <section class="content-section">
            <div class="section-heading">
              <span class="section-index">03</span>
              <div>
                <h2>快速接入</h2>
                <p>只需三个步骤，即可完成从客户端申请到接口调用。</p>
              </div>
            </div>

            <div class="steps-panel">
              <div class="step-item">
                <span class="step-number">1</span>
                <div><h3>申请客户端</h3><p>联系平台管理员创建 API 客户端，获取 <code>client_id</code> 与 <code>client_secret</code>。</p></div>
              </div>
              <span class="step-line"></span>
              <div class="step-item">
                <span class="step-number">2</span>
                <div><h3>获取访问令牌</h3><p>使用客户端凭据调用 OAuth Token 端点，换取 Bearer 访问令牌。</p></div>
              </div>
              <span class="step-line"></span>
              <div class="step-item">
                <span class="step-number">3</span>
                <div><h3>调用开放接口</h3><p>在请求头携带访问令牌，即可调用当前已开放的电站接口。</p></div>
              </div>
            </div>
          </section>

          <button type="button" class="next-doc" @click="selectSection('security')">
            <span><small>下一篇</small><strong>安全鉴权</strong></span>
            <svg viewBox="0 0 20 20" aria-hidden="true"><path d="M4 10h11M11 6l4 4-4 4"></path></svg>
          </button>
        </article>

        <article v-else-if="activeSection === 'security'" class="docs-article security-article">
          <div class="breadcrumb"><span>开放平台文档</span><i>/</i>安全鉴权</div>

          <section class="article-title">
            <span class="eyebrow">AUTHENTICATION</span>
            <h1>安全鉴权</h1>
            <p>开放接口采用 OAuth 2.0 客户端凭据模式，适用于可信服务端之间的系统集成。</p>
          </section>

          <div class="security-callout">
            <span class="callout-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24"><path d="M12 3 4.5 6v5.5c0 4.6 3.2 8.1 7.5 9.5 4.3-1.4 7.5-4.9 7.5-9.5V6L12 3Z"></path><path d="m8.5 12 2.2 2.2 4.8-5"></path></svg>
            </span>
            <div><strong>OAuth 2.0 Client Credentials</strong><p>客户端密钥仅用于服务端换取令牌，请勿放入浏览器、移动端或任何公开代码中。</p></div>
          </div>

          <section class="content-section auth-flow-section">
            <div class="section-heading">
              <span class="section-index">01</span>
              <div><h2>鉴权流程</h2><p>客户端凭据换取短期令牌，再使用令牌访问业务资源。</p></div>
            </div>

            <div class="auth-flow">
              <div class="flow-node">
                <span class="flow-number">1</span>
                <div class="flow-icon"><svg viewBox="0 0 24 24"><path d="M8 11V8a4 4 0 0 1 8 0v3M6 11h12v9H6zM12 15v2"></path></svg></div>
                <strong>客户端凭据</strong><small>client_id + client_secret</small>
              </div>
              <div class="flow-arrow"><span>HTTP Basic</span><svg viewBox="0 0 40 12"><path d="M1 6h36M32 1l5 5-5 5"></path></svg></div>
              <div class="flow-node">
                <span class="flow-number">2</span>
                <div class="flow-icon"><svg viewBox="0 0 24 24"><path d="M4 6h16v12H4zM8 10h8M8 14h5"></path></svg></div>
                <strong>Token 端点</strong><small>POST /api/open/oauth/token</small>
              </div>
              <div class="flow-arrow"><span>access_token</span><svg viewBox="0 0 40 12"><path d="M1 6h36M32 1l5 5-5 5"></path></svg></div>
              <div class="flow-node">
                <span class="flow-number">3</span>
                <div class="flow-icon"><svg viewBox="0 0 24 24"><path d="M5 4h14v16H5zM8 8h8M8 12h8M8 16h5"></path></svg></div>
                <strong>开放接口</strong><small>Authorization: Bearer</small>
              </div>
            </div>
          </section>

          <section class="content-section">
            <div class="section-heading">
              <span class="section-index">02</span>
              <div><h2>获取访问令牌</h2><p>向 Token 端点发送表单请求，客户端凭据通过 HTTP Basic 请求头传递。</p></div>
            </div>

            <div class="endpoint-card">
              <span class="method post">POST</span>
              <code>/api/open/oauth/token</code>
              <span class="endpoint-auth">无需 Bearer Token</span>
            </div>

            <h3 class="subheading">请求规范</h3>
            <div class="spec-grid">
              <div><span>Content-Type</span><code>application/x-www-form-urlencoded</code></div>
              <div><span>Authorization</span><code>Basic Base64(client_id:client_secret)</code></div>
              <div><span>grant_type</span><code>client_credentials</code></div>
              <div><span>请求方式</span><code>POST</code></div>
            </div>

            <div class="code-block">
              <div class="code-header"><span>cURL</span><button type="button" @click="copyCode(tokenRequest, 'token')">{{ copiedBlock === 'token' ? '已复制' : '复制' }}</button></div>
              <pre><code>{{ tokenRequest }}</code></pre>
            </div>

            <h3 class="subheading response-heading">成功响应</h3>
            <div class="code-block light-code">
              <div class="code-header"><span>200 OK · application/json</span><button type="button" @click="copyCode(tokenResponse, 'response')">{{ copiedBlock === 'response' ? '已复制' : '复制' }}</button></div>
              <pre><code>{{ tokenResponse }}</code></pre>
            </div>

            <div class="field-list">
              <div><code>access_token</code><span>用于访问开放接口的 JWT，请按敏感凭据保存。</span></div>
              <div><code>token_type</code><span>固定为 <strong>Bearer</strong>。</span></div>
              <div><code>expires_in</code><span>令牌有效期，当前为 <strong>7200 秒（2 小时）</strong>。</span></div>
            </div>
          </section>

          <section class="content-section">
            <div class="section-heading">
              <span class="section-index">03</span>
              <div><h2>携带令牌调用接口</h2><p>在每个开放业务请求中添加标准 Bearer Authorization 请求头。</p></div>
            </div>

            <div class="code-block">
              <div class="code-header"><span>cURL</span><button type="button" @click="copyCode(apiRequest, 'api')">{{ copiedBlock === 'api' ? '已复制' : '复制' }}</button></div>
              <pre><code>{{ apiRequest }}</code></pre>
            </div>

            <div class="info-note">
              <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9"></circle><path d="M12 10v6M12 7h.01"></path></svg>
              <p>令牌校验同时检查签名、签发方、接收方、有效期、客户端状态及租户归属。客户端被禁用后，已签发令牌也将无法继续使用。</p>
            </div>
          </section>

          <button type="button" class="next-doc" @click="selectSection('idempotency')">
            <span><small>下一篇</small><strong>幂等请求</strong></span>
            <svg viewBox="0 0 20 20" aria-hidden="true"><path d="M4 10h11M11 6l4 4-4 4"></path></svg>
          </button>
        </article>

        <article v-else-if="activeSection === 'idempotency'" class="docs-article idempotency-article">
          <div class="breadcrumb"><span>开放平台文档</span><i>/</i>幂等请求</div>

          <section class="article-title">
            <span class="eyebrow">IDEMPOTENT REQUESTS</span>
            <h1>幂等请求</h1>
            <p>通过 <code>Idempotency-Key</code> 安全重试写操作，避免因网络超时或重复提交而多次创建同一业务资源。</p>
          </section>

          <div class="idempotency-callout">
            <span class="callout-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24"><path d="M7.5 7.5A6.5 6.5 0 1 1 5.6 13M3.5 7.5h4v4"></path><path d="M9.5 12.5 11.3 14l3.4-4"></path></svg>
            </span>
            <div>
              <strong>一次业务操作，一个幂等键</strong>
              <p>首次请求没有收到明确结果时，请保持请求参数不变并复用原键重试；发起新的业务操作时必须生成新键。</p>
            </div>
          </div>

          <section class="content-section">
            <div class="section-heading">
              <span class="section-index">01</span>
              <div><h2>什么时候需要幂等键</h2><p>仅在接口详情明确标记必须提供幂等键时添加该请求头。</p></div>
            </div>

            <div class="error-table-wrap">
              <table class="error-table idempotency-scope-table">
                <thead><tr><th>接口</th><th>要求</th><th>说明</th></tr></thead>
                <tbody>
                  <tr>
                    <td><code>POST /api/open/power-station</code></td>
                    <td><span class="idempotency-badge">必须</span></td>
                    <td>新增电站可能产生新资源，必须携带 <code>Idempotency-Key</code>。</td>
                  </tr>
                  <tr>
                    <td>其他开放接口</td>
                    <td><span class="idempotency-badge optional">按文档</span></td>
                    <td>以接口详情中的 Header 参数和“该接口必须提供幂等键”提示为准。</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section class="content-section">
            <div class="section-heading">
              <span class="section-index">02</span>
              <div><h2>生成与复用规则</h2><p>幂等键由调用方生成，并在同一次业务操作的所有重试中保持不变。</p></div>
            </div>

            <div class="practice-grid idempotency-rules">
              <div><strong>使用高唯一性的随机值</strong><p>推荐使用 UUID v4，例如 <code>7f47f8c8-91c2-4e7b-88e0-213c2cfcbe85</code>，降低不同请求发生碰撞的概率。</p></div>
              <div><strong>重试必须复用原键</strong><p>连接中断、读取超时或未收到明确结果时，以完全相同的业务参数和幂等键重新发送请求。</p></div>
              <div><strong>新操作必须使用新键</strong><p>即使请求地址相同，只要业务意图是再创建一个资源，就应生成新的幂等键。</p></div>
              <div><strong>键中不要放敏感信息</strong><p>不要使用手机号、邮箱、证件号或密钥作为幂等键；随机标识足以表达请求身份。</p></div>
            </div>
          </section>

          <section class="content-section">
            <div class="section-heading">
              <span class="section-index">03</span>
              <div><h2>请求示例</h2><p>将幂等键放在标准请求头中，与 Bearer Token 和 JSON 请求体一同发送。</p></div>
            </div>

            <div class="code-block">
              <div class="code-header"><span>cURL</span><button type="button" @click="copyCode(idempotencyRequest, 'idempotency')">{{ copiedBlock === 'idempotency' ? '已复制' : '复制' }}</button></div>
              <pre><code>{{ idempotencyRequest }}</code></pre>
            </div>

            <div class="info-note">
              <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9"></circle><path d="M12 10v6M12 7h.01"></path></svg>
              <p>不要在每次自动重试时重新生成键，否则服务端会把重试识别为新的业务请求，无法防止重复创建。</p>
            </div>
          </section>

          <section class="content-section">
            <div class="section-heading">
              <span class="section-index">04</span>
              <div><h2>服务端处理方式</h2><p>幂等范围由当前 API 客户端和幂等键共同确定。</p></div>
            </div>

            <div class="error-table-wrap">
              <table class="error-table idempotency-behavior-table">
                <thead><tr><th>场景</th><th>处理结果</th></tr></thead>
                <tbody>
                  <tr><td>首次请求执行成功</td><td>在业务事务中保存成功业务数据，随后将结果返回给调用方。</td></tr>
                  <tr><td>同一客户端使用相同键重试</td><td>返回首次保存的成功业务数据，不会重复创建电站。</td></tr>
                  <tr><td>不同客户端使用相同键</td><td>按相互独立的请求处理；幂等键不会跨 API 客户端共享。</td></tr>
                  <tr><td>首次请求执行失败</td><td>不保存幂等响应；修正问题后可继续使用原键重试。</td></tr>
                  <tr><td>缺少或传入空白键</td><td>请求被拒绝，平台响应码为 <code>101006</code>。</td></tr>
                  <tr><td>幂等记录有效期</td><td>当前不会自动过期；新的业务操作必须始终使用新键。</td></tr>
                </tbody>
              </table>
            </div>

            <div class="idempotency-warning">
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3 2.8 20h18.4L12 3Z"></path><path d="M12 9v5M12 17h.01"></path></svg>
              <p><strong>不要让同一个键对应不同参数。</strong>当前服务端不会比较重试请求与首次请求的参数；一旦已有成功记录，相同键会直接得到首次响应。</p>
            </div>
          </section>

          <button type="button" class="next-doc" @click="selectSection('response')">
            <span><small>下一篇</small><strong>统一响应</strong></span>
            <svg viewBox="0 0 20 20" aria-hidden="true"><path d="M4 10h11M11 6l4 4-4 4"></path></svg>
          </button>
        </article>

        <ApiDetail
          v-else-if="activeSection === 'api'"
          :endpoint="activeEndpoint"
          :open-api-document="openApiDocument"
          :loading="apiLoading"
          :error="apiLoadError"
          @retry="loadApiDocument"
        />

        <article v-else class="docs-article response-article">
          <div class="breadcrumb"><span>开放平台文档</span><i>/</i>统一响应</div>

          <section class="article-title">
            <span class="eyebrow">RESPONSE &amp; ERRORS</span>
            <h1>统一响应</h1>
            <p>开放资源接口使用统一的 <code>Result&lt;T&gt;</code> 响应；Token 端点遵循 OAuth 2.0 协议，使用独立的令牌与错误格式。</p>
          </section>

          <div class="response-callout">
            <span class="callout-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9"></circle><path d="M12 10v6M12 7h.01"></path></svg>
            </span>
            <div>
              <strong>请按端点类型选择解析方式</strong>
              <p><code>/api/open/oauth/token</code> 不使用 <code>Result&lt;T&gt;</code> 包装；其余 <code>/api/open/**</code> 开放资源可统一按 <code>Result&lt;T&gt;</code> 解析。</p>
            </div>
          </div>

          <section class="content-section">
            <div class="section-heading">
              <span class="section-index">01</span>
              <div><h2>两类响应的区别</h2><p>Token 端点服务于标准 OAuth 客户端，开放资源则使用平台统一业务协议。</p></div>
            </div>

            <div class="error-table-wrap">
              <table class="error-table response-table">
                <thead>
                  <tr><th>对比项</th><th>Token 端点</th><th>开放资源接口</th></tr>
                </thead>
                <tbody>
                  <tr><td>接口范围</td><td><code>POST /api/open/oauth/token</code></td><td>除 Token 端点外的 <code>/api/open/**</code></td></tr>
                  <tr><td>成功响应</td><td>直接返回 <code>access_token</code>、<code>token_type</code>、<code>expires_in</code></td><td>使用 <code>Result&lt;T&gt;</code>，业务数据放在 <code>data</code></td></tr>
                  <tr><td>错误响应</td><td>OAuth 的 <code>error</code> 与 <code>error_description</code></td><td>平台统一的 <code>code</code>、<code>result</code>、<code>data</code>、<code>message</code>、<code>traceId</code></td></tr>
                  <tr><td>认证挑战</td><td><code>WWW-Authenticate: Basic</code></td><td><code>WWW-Authenticate: Bearer</code></td></tr>
                  <tr><td>判断依据</td><td colspan="2">先判断 HTTP 状态，再按端点类型解析响应体。</td></tr>
                </tbody>
              </table>
            </div>
          </section>

          <section class="content-section">
            <div class="section-heading">
              <span class="section-index">02</span>
              <div><h2>错误响应示例</h2><p>两类错误的字段不同，但都会保留有意义的 HTTP 状态。</p></div>
            </div>

            <div class="response-example-grid">
              <div class="response-example">
                <div class="example-heading">
                  <span class="response-type oauth">OAuth 格式</span>
                  <div><h3>Token 端点错误</h3><p>示例：客户端凭据无效</p></div>
                </div>
                <div class="code-block light-code compact-code">
                  <div class="code-header"><span>401 Unauthorized</span><button type="button" @click="copyCode(tokenErrorResponse, 'tokenError')">{{ copiedBlock === 'tokenError' ? '已复制' : '复制' }}</button></div>
                  <pre><code>{{ tokenErrorResponse }}</code></pre>
                </div>
                <p class="response-hint">同时返回 <code>WWW-Authenticate: Basic realm="open-api-token"</code>。</p>
              </div>

              <div class="response-example">
                <div class="example-heading">
                  <span class="response-type result">Result&lt;T&gt;</span>
                  <div><h3>开放资源错误</h3><p>示例：Bearer Token 无效或过期</p></div>
                </div>
                <div class="code-block light-code compact-code">
                  <div class="code-header"><span>401 Unauthorized</span><button type="button" @click="copyCode(resourceErrorResponse, 'resourceError')">{{ copiedBlock === 'resourceError' ? '已复制' : '复制' }}</button></div>
                  <pre><code>{{ resourceErrorResponse }}</code></pre>
                </div>
                <p class="response-hint">同时返回 <code>WWW-Authenticate: Bearer ... error="invalid_token"</code>。</p>
              </div>
            </div>
          </section>

          <section class="content-section">
            <div class="section-heading">
              <span class="section-index">03</span>
              <div><h2>Token 端点错误</h2><p>Token 端点使用 OAuth 标准错误标识，不返回平台业务错误码。</p></div>
            </div>

            <div class="error-table-wrap">
              <table class="error-table token-error-table">
                <thead><tr><th>HTTP 状态</th><th>error</th><th>常见场景</th></tr></thead>
                <tbody>
                  <tr><td><span class="status-code warn">400</span></td><td><code>invalid_request</code></td><td>缺少 <code>grant_type</code> 等必要参数。</td></tr>
                  <tr><td><span class="status-code warn">400</span></td><td><code>unsupported_grant_type</code></td><td><code>grant_type</code> 不是 <code>client_credentials</code>。</td></tr>
                  <tr><td><span class="status-code danger">401</span></td><td><code>invalid_client</code></td><td>Basic Header 缺失或损坏、客户端不存在或禁用、密钥错误。</td></tr>
                  <tr><td><span class="status-code warn">415</span></td><td><code>invalid_request</code></td><td>使用 JSON，而非表单格式请求 Token 端点。</td></tr>
                  <tr><td><span class="status-code neutral">500</span></td><td><code>server_error</code></td><td>授权服务内部异常。</td></tr>
                </tbody>
              </table>
            </div>
          </section>

          <section class="content-section">
            <div class="section-heading">
              <span class="section-index">04</span>
              <div><h2>开放资源错误</h2><p>鉴权失败和业务异常均可复用同一套 <code>Result&lt;T&gt;</code> 响应体解析逻辑。</p></div>
            </div>

            <div class="error-table-wrap">
              <table class="error-table response-field-table">
                <thead><tr><th>字段</th><th>数据类型</th><th>说明</th></tr></thead>
                <tbody>
                  <tr><td><code>code</code></td><td><code>integer</code></td><td>平台响应码，<strong>1 代表成功，其他值代表失败</strong>。失败时可根据下方错误码列表定位原因。</td></tr>
                  <tr><td><code>result</code></td><td><code>boolean</code></td><td>请求是否成功；错误响应固定为 <strong>false</strong>。</td></tr>
                  <tr><td><code>data</code></td><td><code>any</code></td><td>业务数据，具体类型和结构由接口定义；错误响应通常为 <strong>null</strong>。</td></tr>
                  <tr><td><code>message</code></td><td><code>string</code></td><td>面向调用方的错误说明，不包含服务端内部异常信息。</td></tr>
                  <tr><td><code>traceId</code></td><td><code>string</code></td><td>链路追踪 ID。</td></tr>
                </tbody>
              </table>
            </div>

            <div class="info-note">
              <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9"></circle><path d="M12 10v6M12 7h.01"></path></svg>
              <p>
                开放资源接口使用 HTTP 状态区分错误类型：<strong>4xx 表示业务错误或请求错误，5xx 表示系统错误</strong>。
              </p>
            </div>

            <h3 class="subheading response-code-heading">错误码</h3>
            <div class="error-table-wrap">
              <table class="error-table response-code-table">
                <thead><tr><th>响应码</th><th>说明</th></tr></thead>
                <tbody>
                  <tr><td><code>100001</code></td><td>访问令牌无效或已过期。</td></tr>
                </tbody>
              </table>
            </div>
          </section>
        </article>

        <footer class="docs-footer">
          <span>Solar Energy 开放平台</span>
        </footer>
      </main>
    </div>

    <transition name="toast">
      <div v-if="copiedBlock" class="copy-toast">代码已复制到剪贴板</div>
    </transition>
  </div>
</template>

<script>
import ApiDetail from './ApiDetail.vue';

export default {
  name: 'OpenPlatformDocs',
  components: { ApiDetail },
  data() {
    return {
      copiedBlock: '',
      copyTimer: null,
      openApiDocument: null,
      apiGroups: [],
      apiLoading: false,
      apiLoadError: '',
      apiCatalogExpanded: false,
      expandedApiGroups: {},
      tokenRequest: [
        "curl --request POST 'https://gf.hxszkj.cn/api/open/oauth/token' \\",
        "  --header 'Authorization: Basic BASE64(client_id:client_secret)' \\",
        "  --header 'Content-Type: application/x-www-form-urlencoded' \\",
        "  --data-urlencode 'grant_type=client_credentials'"
      ].join('\n'),
      tokenResponse: [
        '{',
        '  "access_token": "eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9...",',
        '  "token_type": "Bearer",',
        '  "expires_in": 7200',
        '}'
      ].join('\n'),
      apiRequest: [
        "curl --request GET 'https://gf.hxszkj.cn/api/open/power-station/1001' \\",
        "  --header 'Authorization: Bearer {access_token}' \\",
        "  --header 'Accept: application/json'"
      ].join('\n'),
      idempotencyRequest: [
        "curl --request POST 'https://gf.hxszkj.cn/api/open/power-station' \\",
        "  --header 'Authorization: Bearer {access_token}' \\",
        "  --header 'Idempotency-Key: 7f47f8c8-91c2-4e7b-88e0-213c2cfcbe85' \\",
        "  --header 'Content-Type: application/json' \\",
        "  --data '{",
        "    \"companyName\": \"示例能源有限公司\",",
        "    \"stationName\": \"园区屋顶电站\",",
        "    \"stationType\": 1,",
        "    \"investBody\": \"示例能源有限公司\",",
        "    \"consumptionType\": 1,",
        "    \"userName\": \"张三\",",
        "    \"linkMobile\": \"13800000000\",",
        "    \"installedCapacity\": 500,",
        "    \"priceOfMoney\": 0.42,",
        "    \"stationAddress\": \"示例市新能源产业园\"",
        "  }'"
      ].join('\n'),
      tokenErrorResponse: [
        '{',
        '  "error": "invalid_client",',
        '  "error_description": "Client authentication failed"',
        '}'
      ].join('\n'),
      resourceErrorResponse: [
        '{',
        '  "code": 100001,',
        '  "result": false,',
        '  "data": null,',
        '  "message": "The access token is invalid or expired",',
        '  "traceId": "4f1c2d3e5a6b7890cdef123456789abc"',
        '}'
      ].join('\n')
    };
  },
  computed: {
    activeSection() {
      return this.$route.meta.openPlatformSection || 'introduction';
    },
    apiEndpointCount() {
      const identities = {};
      this.apiGroups.forEach(group => {
        group.endpoints.forEach(endpoint => {
          identities[endpoint.routeKey] = true;
        });
      });
      return Object.keys(identities).length;
    },
    activeEndpoint() {
      if (this.activeSection !== 'api') return null;
      const operationId = this.$route.params.operationId;
      for (let groupIndex = 0; groupIndex < this.apiGroups.length; groupIndex += 1) {
        const group = this.apiGroups[groupIndex];
        for (let endpointIndex = 0; endpointIndex < group.endpoints.length; endpointIndex += 1) {
          const endpoint = group.endpoints[endpointIndex];
          if (endpoint.routeKey === operationId) return endpoint;
        }
      }
      return null;
    }
  },
  watch: {
    '$route'(to, from) {
      if (to.fullPath !== from.fullPath) {
        this.copiedBlock = '';
        this.scrollToTop();
      }
      if (to.meta.openPlatformSection === 'api') {
        this.apiCatalogExpanded = true;
        this.expandActiveApiGroup();
      }
      this.updateDocumentTitle();
    }
  },
  mounted() {
    this.updateDocumentTitle();
    this.loadApiDocument();
  },
  beforeDestroy() {
    if (this.copyTimer) {
      clearTimeout(this.copyTimer);
    }
  },
  methods: {
    loadApiDocument() {
      if (this.apiLoading) return;
      this.apiLoading = true;
      this.apiLoadError = '';
      Promise.all([
        this.$request.get('/v3/api-docs/open'),
        this.$request.get('/api/open-api-doc/endpoint/list')
      ]).then(([openApiResponse, extensionResponse]) => {
        const document = openApiResponse && openApiResponse.data;
        const extensionResult = extensionResponse && extensionResponse.data;
        if (!document || !document.paths) {
          throw new Error('接口返回的 OpenAPI 文档格式不正确');
        }
        if (!extensionResult || !extensionResult.result) {
          throw new Error((extensionResult && extensionResult.message) || '接口扩展文档加载失败');
        }
        this.openApiDocument = document;
        this.apiGroups = this.buildApiGroups(document, extensionResult.data || []);
        this.expandedApiGroups = {};
        if (this.activeEndpoint) {
          this.apiCatalogExpanded = true;
        }
        this.expandActiveApiGroup();
        this.updateDocumentTitle();
      }).catch(error => {
        const status = error && error.response && error.response.status;
        this.apiLoadError = status ? '请求失败（HTTP ' + status + '）' : (error.message || '无法连接接口文档服务');
      }).then(() => {
        this.apiLoading = false;
      });
    },
    buildApiGroups(document, extensions) {
      const httpMethods = ['get', 'post', 'put', 'patch', 'delete', 'head', 'options', 'trace'];
      const groups = {};
      const discoveryOrder = [];
      const paths = document.paths || {};
      const extensionMap = {};
      extensions.forEach(item => {
        extensionMap[item.operationId] = item;
      });

      Object.keys(paths).forEach(path => {
        const pathItem = paths[path] || {};
        httpMethods.forEach(method => {
          const operation = pathItem[method];
          if (!operation) return;
          const tags = operation.tags && operation.tags.length ? operation.tags : ['未分组'];
          const routeKey = operation.operationId || this.createApiRouteKey(method, path);
          const extension = extensionMap[operation.operationId] || {};
          tags.forEach(tag => {
            if (!groups[tag]) {
              groups[tag] = [];
              discoveryOrder.push(tag);
            }
            groups[tag].push({
              routeKey: routeKey,
              operationId: operation.operationId || '',
              method: method.toUpperCase(),
              path: path,
              summary: operation.summary || operation.operationId || method.toUpperCase() + ' ' + path,
              groupName: tag,
              operation: operation,
              description: extension.description || operation.description || '',
              notes: extension.notes || '',
              requestExamples: extension.requestExamples || [],
              responseExamples: extension.responseExamples || []
            });
          });
        });
      });

      const declaredOrder = (document.tags || []).map(tag => tag.name).filter(name => groups[name]);
      const orderedNames = declaredOrder.concat(discoveryOrder.filter(name => declaredOrder.indexOf(name) === -1));
      return orderedNames.map(name => ({ name: name, endpoints: groups[name] }));
    },
    createApiRouteKey(method, path) {
      return (method + '-' + path).replace(/[^a-zA-Z0-9_-]+/g, '-').replace(/^-|-$/g, '');
    },
    toggleApiGroup(groupName) {
      this.$set(this.expandedApiGroups, groupName, !this.isApiGroupExpanded(groupName));
    },
    isApiGroupExpanded(groupName) {
      return this.expandedApiGroups[groupName] === true;
    },
    expandActiveApiGroup() {
      if (!this.activeEndpoint) return;
      this.$set(this.expandedApiGroups, this.activeEndpoint.groupName, true);
    },
    selectApi(endpoint) {
      if (this.$route.name === 'openPlatformApiDetail' && this.$route.params.operationId === endpoint.routeKey) {
        this.scrollToTop();
        return;
      }
      this.$router.push({ name: 'openPlatformApiDetail', params: { operationId: endpoint.routeKey } });
    },
    updateDocumentTitle() {
      this.$nextTick(() => {
        document.title = this.activeEndpoint
          ? this.activeEndpoint.summary + ' - 开放平台文档'
          : (this.$route.meta.title || '开放平台文档');
      });
    },
    selectSection(section) {
      const routeNames = {
        introduction: 'openPlatformIntroduction',
        security: 'openPlatformSecurity',
        idempotency: 'openPlatformIdempotency',
        response: 'openPlatformResponse'
      };
      const routeName = routeNames[section];
      if (!routeName || this.$route.name === routeName) {
        this.scrollToTop();
        return;
      }
      this.$router.push({ name: routeName });
    },
    scrollToTop() {
      this.$nextTick(() => {
        if (this.$refs.docsMain) {
          this.$refs.docsMain.scrollTop = 0;
        }
      });
    },
    copyCode(content, block) {
      const markCopied = () => {
        this.copiedBlock = block;
        if (this.copyTimer) {
          clearTimeout(this.copyTimer);
        }
        this.copyTimer = setTimeout(() => {
          this.copiedBlock = '';
        }, 1800);
      };

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(content).then(markCopied).catch(() => {
          this.copyWithTextarea(content, markCopied);
        });
        return;
      }
      this.copyWithTextarea(content, markCopied);
    },
    copyWithTextarea(content, callback) {
      const textarea = document.createElement('textarea');
      textarea.value = content;
      textarea.setAttribute('readonly', 'readonly');
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      callback();
    }
  }
};
</script>

<style scoped>
.open-platform-docs {
  --ink: #172033;
  --muted: #667085;
  --line: #e7e9ee;
  --soft: #f7f8fa;
  --amber: #f59e0b;
  --amber-deep: #d97706;
  --amber-soft: #fff7e6;
  width: 100%;
  height: 100%;
  overflow: hidden;
  color: var(--ink);
  background: #ffffff;
  font-family: Inter, "PingFang SC", "Microsoft YaHei", Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
}

.open-platform-docs *,
.open-platform-docs *::before,
.open-platform-docs *::after { box-sizing: border-box; }

.docs-header {
  position: relative;
  z-index: 10;
  height: 68px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 32px;
  border-bottom: 1px solid var(--line);
  background: rgba(255, 255, 255, 0.98);
}

.brand, .header-actions, .console-link { display: flex; align-items: center; }
.brand-mark {
  width: 34px;
  height: 34px;
  display: grid;
  place-items: center;
  margin-right: 11px;
  color: #fff;
  border-radius: 10px;
  background: var(--amber);
  box-shadow: 0 8px 18px rgba(245, 158, 11, 0.24);
}
.brand-mark svg { width: 24px; height: 24px; fill: none; stroke: currentColor; stroke-width: 2; stroke-linecap: round; }
.brand-name { font-size: 17px; font-weight: 750; letter-spacing: -0.3px; }
.brand-divider { width: 1px; height: 18px; margin: 0 14px; background: #d9dde5; }
.brand-product { font-size: 14px; color: #555f73; }
.header-actions { gap: 22px; }
.console-link { gap: 5px; color: #475467; font-size: 13px; text-decoration: none; transition: color .2s ease; }
.console-link:hover { color: var(--amber-deep); }
.console-link svg { width: 16px; height: 16px; fill: none; stroke: currentColor; stroke-width: 1.8; }

.docs-layout { height: calc(100% - 68px); display: flex; }
.docs-sidebar { width: 272px; flex: 0 0 272px; border-right: 1px solid var(--line); background: #fafafa; }
.sidebar-inner { height: 100%; display: flex; flex-direction: column; padding: 34px 20px 24px; overflow-y: auto; scrollbar-color: #cfd3da #fafafa; scrollbar-width: thin; }
.sidebar-inner::-webkit-scrollbar { width: 8px; background: #fafafa; }
.sidebar-inner::-webkit-scrollbar-track { background: #fafafa; }
.sidebar-inner::-webkit-scrollbar-thumb { border: 2px solid #fafafa; border-radius: 8px; background: #cfd3da; }
.sidebar-inner::-webkit-scrollbar-thumb:hover { background: #d9a348; }
.nav-caption { padding: 0 12px 10px; color: #98a2b3; font-size: 11px; font-weight: 750; letter-spacing: 1.2px; text-transform: uppercase; }
.docs-nav { display: flex; flex-direction: column; gap: 6px; }
.nav-item {
  position: relative;
  width: 100%;
  display: grid;
  grid-template-columns: 38px 1fr 18px;
  align-items: center;
  gap: 10px;
  padding: 12px;
  color: #596174;
  text-align: left;
  border: 1px solid transparent;
  border-radius: 10px;
  background: transparent;
  cursor: pointer;
  transition: color .2s ease, border-color .2s ease, background .2s ease, box-shadow .2s ease;
}
.nav-item:hover { color: var(--ink); background: #fff; border-color: #eceef2; }
.nav-item.active { color: #9a5b00; background: #fff9ed; border-color: #f7d99b; box-shadow: 0 7px 18px rgba(123, 82, 19, 0.06); }
.nav-icon { width: 38px; height: 38px; display: grid; place-items: center; color: #667085; border-radius: 9px; background: #f0f1f3; }
.active .nav-icon { color: #b66b00; background: #ffedc7; }
.nav-icon svg { width: 21px; height: 21px; fill: none; stroke: currentColor; stroke-width: 1.7; stroke-linecap: round; stroke-linejoin: round; }
.nav-item strong { display: block; margin-bottom: 4px; font-size: 14px; font-weight: 700; }
.nav-item small { display: block; color: #98a2b3; font-size: 11px; font-weight: 400; }
.nav-arrow { width: 16px; height: 16px; fill: none; stroke: #b7bdc8; stroke-width: 1.7; opacity: 0; transform: translateX(-4px); transition: all .2s ease; }
.nav-item.active .nav-arrow { opacity: 1; transform: translateX(0); stroke: #cf7a00; }
.api-nav-caption { margin-top: 28px; }
.api-catalog { display: flex; flex-direction: column; }
.api-catalog-root .nav-arrow { opacity: 1; transform: rotate(0); }
.api-catalog-root .nav-arrow.expanded { transform: rotate(90deg); }
.api-catalog-tree { margin: 8px 0 0 19px; padding: 2px 0 2px 14px; border-left: 1px solid #e1e4e9; }
.catalog-state { padding: 10px 8px; color: #98a2b3; font-size: 10px; line-height: 1.5; }
.catalog-retry { width: 100%; text-align: left; border: 0; background: transparent; cursor: pointer; }
.catalog-retry:hover { color: #b66b00; }
.api-group + .api-group { margin-top: 4px; }
.api-group-title { width: 100%; display: grid; grid-template-columns: 1fr auto 14px; align-items: center; gap: 7px; padding: 7px 7px; color: #596174; text-align: left; border: 0; border-radius: 6px; background: transparent; cursor: pointer; }
.api-group-title:hover { color: #30384a; background: #f2f3f5; }
.api-group-title > span { overflow: hidden; font-size: 11px; font-weight: 700; text-overflow: ellipsis; white-space: nowrap; }
.api-group-title small { min-width: 18px; padding: 2px 5px; color: #8b93a2; border-radius: 8px; background: #eceef2; font-size: 9px; text-align: center; }
.api-group-title svg { width: 13px; height: 13px; fill: none; stroke: #98a2b3; stroke-width: 1.8; transform: rotate(0); transition: transform .2s ease; }
.api-group-title svg.expanded { transform: rotate(90deg); }
.api-endpoint-list { display: flex; flex-direction: column; gap: 2px; margin: 2px 0 5px; }
.api-endpoint-item { width: 100%; display: grid; grid-template-columns: 38px 1fr; align-items: center; gap: 7px; padding: 7px; color: #697386; text-align: left; border: 1px solid transparent; border-radius: 6px; background: transparent; cursor: pointer; }
.api-endpoint-item:hover { color: #30384a; background: #fff; }
.api-endpoint-item.active { color: #8a5100; border-color: #f4ddb0; background: #fff9ed; }
.api-endpoint-item > span:last-child { overflow: hidden; font-size: 10px; text-overflow: ellipsis; white-space: nowrap; }
.endpoint-method { min-width: 36px; padding: 3px 4px; border-radius: 4px; font-size: 7px; font-weight: 800; letter-spacing: .2px; text-align: center; }
.endpoint-method.post { color: #9a6108; background: #fff0cc; }
.endpoint-method.put, .endpoint-method.patch { color: #2765a4; background: #e8f2ff; }
.endpoint-method.get { color: #18734e; background: #e5f6ef; }
.endpoint-method.delete { color: #b13d46; background: #fdecee; }
.docs-main { flex: 1; min-width: 0; height: 100%; overflow: auto; background: #fff; scroll-behavior: smooth; }
.docs-main::-webkit-scrollbar { width: 8px; background: #fff; }
.docs-main::-webkit-scrollbar-thumb { border: 2px solid #fff; border-radius: 8px; background: #d9dde4; }
.docs-main::-webkit-scrollbar-track { background: #fff; }
.docs-article { width: min(1060px, calc(100% - 80px)); margin: 0 auto; padding: 34px 0 70px; }
.breadcrumb { display: flex; align-items: center; gap: 9px; margin-bottom: 38px; color: #98a2b3; font-size: 12px; }
.breadcrumb span { color: #667085; }
.breadcrumb i { color: #d0d5dd; font-style: normal; }

.hero-section { min-height: 360px; display: grid; grid-template-columns: 1.14fr .86fr; gap: 46px; align-items: center; padding: 42px 48px; overflow: hidden; border: 1px solid #ece8df; border-radius: 18px; background: #fffdf8; }
.hero-copy { position: relative; z-index: 2; }
.eyebrow { display: inline-flex; align-items: center; gap: 8px; color: #b56a00; font-size: 11px; font-weight: 800; letter-spacing: 1.45px; }
.eyebrow::before { content: ""; width: 22px; height: 2px; background: var(--amber); }
.hero-copy h1, .article-title h1 { margin: 17px 0 16px; color: #151b2a; font-size: clamp(34px, 3.6vw, 50px); line-height: 1.16; letter-spacing: -1.7px; }
.hero-copy p, .article-title p { max-width: 600px; margin: 0; color: #667085; font-size: 15px; line-height: 1.9; }
.primary-action { display: inline-flex; align-items: center; gap: 8px; margin-top: 28px; padding: 11px 18px; color: #fff; border: 0; border-radius: 9px; background: #e89208; box-shadow: 0 9px 22px rgba(217, 119, 6, .2); cursor: pointer; font-size: 13px; font-weight: 700; }
.primary-action:hover { background: #d98100; }
.primary-action svg, .next-doc svg { width: 18px; height: 18px; fill: none; stroke: currentColor; stroke-width: 1.8; }
.hero-visual { position: relative; min-height: 272px; }
.solar-sun { position: absolute; z-index: 2; left: 50%; top: 50%; width: 102px; height: 102px; display: grid; place-items: center; color: #f7a711; border: 1px solid #f6d58c; border-radius: 50%; background: #fff8df; transform: translate(-50%, -50%); box-shadow: 0 0 0 13px rgba(255, 246, 216, .8), 0 16px 34px rgba(182, 109, 0, .15); }
.solar-sun svg { width: 62px; height: 62px; fill: none; stroke: currentColor; stroke-width: 2; stroke-linecap: round; }
.sun-orbit { position: absolute; left: 50%; top: 50%; border: 1px dashed #eccf95; border-radius: 50%; transform: translate(-50%, -50%); }
.orbit-one { width: 218px; height: 218px; }
.orbit-two { width: 306px; height: 306px; opacity: .65; }
.content-section { padding-top: 70px; }
.section-heading { display: flex; align-items: flex-start; gap: 18px; margin-bottom: 26px; }
.section-index {
  min-width: 44px;
  padding-top: 1px;
  color: #c97700;
  font-family: Georgia, serif;
  font-size: 20px;
  font-weight: 700;
  font-style: italic;
  line-height: 1.25;
}
.section-heading h2 { margin: 0 0 8px; color: #1d2535; font-size: 25px; line-height: 1.25; letter-spacing: -.5px; }
.section-heading p { margin: 0; color: #7a8292; font-size: 13px; line-height: 1.7; }
.capability-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; }
.capability-card { min-height: 210px; padding: 25px; border: 1px solid var(--line); border-radius: 13px; background: #fff; transition: transform .2s ease, box-shadow .2s ease; }
.capability-card:hover { transform: translateY(-3px); box-shadow: 0 13px 26px rgba(26, 36, 50, .07); }
.capability-icon { width: 42px; height: 42px; display: grid; place-items: center; border-radius: 10px; }
.capability-icon svg { width: 23px; height: 23px; fill: none; stroke: currentColor; stroke-width: 1.7; stroke-linecap: round; stroke-linejoin: round; }
.capability-icon.amber { color: #bd7200; background: #fff3d6; }
.capability-icon.green { color: #18855a; background: #e8f7f1; }
.capability-icon.blue { color: #3b68b0; background: #eaf1fd; }
.capability-card h3 { margin: 19px 0 9px; font-size: 16px; }
.capability-card p { margin: 0; color: #727b8d; font-size: 12px; line-height: 1.8; }
.steps-panel { display: flex; align-items: flex-start; padding: 28px; border: 1px solid var(--line); border-radius: 13px; background: #fafafa; }
.step-item { flex: 1; display: flex; gap: 14px; }
.step-number { width: 29px; height: 29px; flex: 0 0 29px; display: grid; place-items: center; color: #a76300; border: 1px solid #f3d28c; border-radius: 50%; background: #fff7e6; font-size: 12px; font-weight: 750; }
.step-item h3 { margin: 2px 0 7px; color: #30384a; font-size: 14px; }
.step-item p { margin: 0; color: #7b8392; font-size: 11px; line-height: 1.7; }
.step-item code { color: #a86000; font-family: Consolas, monospace; }
.step-line { width: 42px; height: 1px; flex: 0 0 42px; margin: 14px 20px 0; background: #d9dde4; }
.intro-guide-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; }
.intro-guide-card { min-height: 230px; display: flex; flex-direction: column; align-items: flex-start; padding: 25px; color: inherit; text-align: left; border: 1px solid var(--line); border-radius: 13px; background: #fff; cursor: pointer; transition: transform .2s ease, border-color .2s ease, box-shadow .2s ease; }
.intro-guide-card:hover { transform: translateY(-3px); border-color: #f1d18f; box-shadow: 0 13px 26px rgba(26, 36, 50, .07); }
.guide-label { color: #b56a00; font-size: 9px; font-weight: 800; letter-spacing: 1.2px; }
.intro-guide-card h3 { margin: 16px 0 10px; color: #30384a; font-size: 16px; }
.intro-guide-card p { margin: 0 0 20px; color: #727b8d; font-size: 12px; line-height: 1.8; }
.intro-guide-card code { color: #a25f00; font-family: Consolas, "SFMono-Regular", monospace; font-size: .9em; }
.guide-link { display: inline-flex; align-items: center; gap: 6px; margin-top: auto; color: #a76300; font-size: 11px; font-weight: 700; }
.guide-link svg { width: 15px; height: 15px; fill: none; stroke: currentColor; stroke-width: 1.8; transition: transform .2s ease; }
.intro-guide-card:hover .guide-link svg { transform: translateX(3px); }
.api-table-wrap, .error-table-wrap { overflow: hidden; border: 1px solid var(--line); border-radius: 12px; }
.api-table, .error-table { width: 100%; border-collapse: collapse; background: #fff; }
.api-table th, .error-table th { padding: 13px 17px; color: #7c8493; background: #f8f9fa; font-size: 11px; font-weight: 700; text-align: left; }
.api-table td, .error-table td { padding: 15px 17px; color: #515b6d; border-top: 1px solid #eceef1; font-size: 12px; }
.api-table code, .error-table code, .endpoint-card code, .spec-grid code, .field-list code { color: #424c5e; font-family: Consolas, "SFMono-Regular", monospace; font-size: 11px; }
.method { display: inline-flex; align-items: center; justify-content: center; min-width: 56px; padding: 4px 8px; border-radius: 5px; font-size: 9px; font-weight: 800; letter-spacing: .45px; }
.method.post { color: #9a6108; background: #fff0cc; }
.method.put { color: #2765a4; background: #e8f2ff; }
.method.get { color: #18734e; background: #e5f6ef; }
.method.delete { color: #b13d46; background: #fdecee; }
.next-doc { width: 100%; display: flex; align-items: center; justify-content: space-between; margin-top: 68px; padding: 20px 24px; color: #5d6677; text-align: left; border: 1px solid var(--line); border-radius: 12px; background: #fff; cursor: pointer; }
.next-doc:hover { color: #ae6800; border-color: #f1d18f; background: #fffcf5; }
.next-doc small, .next-doc strong { display: block; }
.next-doc small { margin-bottom: 5px; color: #9ca3af; font-size: 10px; }
.next-doc strong { font-size: 14px; }

.article-title { padding: 24px 0 22px; border-bottom: 1px solid var(--line); }
.article-title h1 { margin: 16px 0 12px; font-size: 46px; }
.article-title code, .response-callout code, .response-hint code, .idempotency-callout code, .idempotency-rules code { color: #a25f00; font-family: Consolas, "SFMono-Regular", monospace; font-size: .9em; }
.security-callout { display: flex; gap: 16px; margin-top: 30px; padding: 20px 22px; border: 1px solid #f1d59b; border-radius: 12px; background: #fff9ec; }
.callout-icon { width: 40px; height: 40px; flex: 0 0 40px; display: grid; place-items: center; color: #b96e00; border-radius: 9px; background: #ffedc3; }
.callout-icon svg { width: 22px; height: 22px; fill: none; stroke: currentColor; stroke-width: 1.8; stroke-linecap: round; stroke-linejoin: round; }
.security-callout strong { color: #69430b; font-size: 14px; }
.security-callout p { margin: 6px 0 0; color: #856830; font-size: 12px; line-height: 1.7; }
.response-callout { display: flex; gap: 16px; margin-top: 30px; padding: 20px 22px; border: 1px solid #cfe1f2; border-radius: 12px; background: #f3f8fd; }
.response-callout .callout-icon { color: #3f719f; background: #e1eef9; }
.response-callout strong { color: #304f6d; font-size: 14px; }
.response-callout p { margin: 6px 0 0; color: #5d7690; font-size: 12px; line-height: 1.7; }
.idempotency-callout { display: flex; gap: 16px; margin-top: 30px; padding: 20px 22px; border: 1px solid #cde8dc; border-radius: 12px; background: #f2faf6; }
.idempotency-callout .callout-icon { color: #18734e; background: #dff3e9; }
.idempotency-callout strong { color: #205c43; font-size: 14px; }
.idempotency-callout p { margin: 6px 0 0; color: #557866; font-size: 12px; line-height: 1.7; }
.idempotency-scope-table th:first-child { width: 280px; }
.idempotency-scope-table th:nth-child(2) { width: 100px; }
.idempotency-badge { display: inline-block; min-width: 44px; padding: 4px 8px; color: #9a6108; border-radius: 5px; background: #fff0cc; font-size: 9px; font-weight: 800; text-align: center; }
.idempotency-badge.optional { color: #4f6175; background: #edf0f3; }
.idempotency-rules p { max-width: 92%; }
.idempotency-behavior-table th:first-child { width: 250px; }
.idempotency-warning { display: flex; gap: 12px; margin-top: 17px; padding: 15px 17px; color: #805018; border: 1px solid #f0d2a0; border-radius: 9px; background: #fff8eb; }
.idempotency-warning svg { width: 19px; height: 19px; flex: 0 0 19px; fill: none; stroke: #b66b00; stroke-width: 1.7; stroke-linecap: round; stroke-linejoin: round; }
.idempotency-warning p { margin: 0; font-size: 11px; line-height: 1.75; }
.auth-flow { display: grid; grid-template-columns: 1fr 110px 1fr 110px 1fr; align-items: center; padding: 28px; border: 1px solid var(--line); border-radius: 13px; background: #fafafa; }
.flow-node { position: relative; min-height: 160px; display: flex; flex-direction: column; align-items: center; justify-content: center; border: 1px solid #e5e8ed; border-radius: 11px; background: #fff; }
.flow-number { position: absolute; left: 10px; top: 9px; color: #b6bdc8; font-size: 10px; font-weight: 750; }
.flow-icon { width: 44px; height: 44px; display: grid; place-items: center; margin-bottom: 13px; color: #bd7200; border-radius: 10px; background: #fff2d5; }
.flow-icon svg { width: 23px; height: 23px; fill: none; stroke: currentColor; stroke-width: 1.7; stroke-linecap: round; stroke-linejoin: round; }
.flow-node strong { color: #333c4e; font-size: 13px; }
.flow-node small { margin-top: 6px; color: #99a1af; font-size: 9px; }
.flow-arrow { display: flex; flex-direction: column; align-items: center; gap: 7px; color: #9da5b2; }
.flow-arrow span { font-size: 9px; }
.flow-arrow svg { width: 40px; height: 12px; fill: none; stroke: #c7ccd4; stroke-width: 1.3; }
.endpoint-card { min-height: 60px; display: flex; align-items: center; gap: 15px; padding: 13px 18px; border: 1px solid var(--line); border-radius: 10px; background: #fafafa; }
.endpoint-card code { color: #30394b; font-size: 12px; font-weight: 650; }
.endpoint-auth { margin-left: auto; color: #858d9b; font-size: 10px; }
.subheading { margin: 31px 0 14px; color: #384154; font-size: 15px; }
.spec-grid { display: grid; grid-template-columns: 1fr 1fr; border: 1px solid var(--line); border-radius: 10px; overflow: hidden; }
.spec-grid div { min-height: 64px; display: flex; flex-direction: column; justify-content: center; gap: 7px; padding: 13px 17px; border-bottom: 1px solid var(--line); }
.spec-grid div:nth-child(odd) { border-right: 1px solid var(--line); }
.spec-grid div:nth-last-child(-n+2) { border-bottom: 0; }
.spec-grid span { color: #8a92a1; font-size: 10px; }
.spec-grid code { color: #3e485a; }
.code-block { margin-top: 18px; overflow: hidden; border: 1px solid #2d3748; border-radius: 11px; background: #172033; box-shadow: 0 10px 24px rgba(22, 30, 48, .1); }
.code-header { height: 42px; display: flex; align-items: center; justify-content: space-between; padding: 0 15px; color: #9da8bb; border-bottom: 1px solid #303a4e; background: #1e283b; font-size: 10px; }
.code-header button { padding: 5px 9px; color: #c8d0dc; border: 1px solid #3d485d; border-radius: 5px; background: transparent; cursor: pointer; font-size: 10px; }
.code-header button:hover { color: #fff; border-color: #667085; }
.code-block pre { margin: 0; padding: 20px; overflow: auto; color: #e7ecf3; background: transparent; font-family: Consolas, "SFMono-Regular", monospace; font-size: 12px; line-height: 1.75; }
.code-block pre::-webkit-scrollbar { height: 6px; background: #172033; }
.code-block pre::-webkit-scrollbar-thumb { background: #4d586c; border-radius: 5px; }
.response-heading { margin-top: 28px; }
.light-code { border-color: #dfe3e9; background: #f7f8fa; box-shadow: none; }
.light-code .code-header { color: #737c8d; border-bottom-color: #e1e4e9; background: #eef0f3; }
.light-code .code-header button { color: #687182; border-color: #cfd4dc; }
.light-code pre { color: #364052; }
.response-table th:first-child { width: 118px; }
.response-table td:first-child { color: #30394b; font-weight: 700; }
.response-table td { line-height: 1.7; }
.response-example-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
.response-example { min-width: 0; padding: 22px; border: 1px solid var(--line); border-radius: 12px; background: #fafafa; }
.example-heading { display: flex; align-items: center; gap: 13px; }
.example-heading h3 { margin: 0 0 5px; color: #30394b; font-size: 14px; }
.example-heading p { margin: 0; color: #8991a0; font-size: 10px; }
.response-type { min-width: 76px; padding: 6px 8px; border-radius: 6px; font-family: Consolas, "SFMono-Regular", monospace; font-size: 9px; font-weight: 750; text-align: center; }
.response-type.oauth { color: #9a6108; background: #fff0cc; }
.response-type.result { color: #2765a4; background: #e8f2ff; }
.compact-code { margin-top: 18px; }
.compact-code pre { min-height: 148px; padding: 17px; font-size: 11px; }
.response-hint { margin: 14px 1px 0; color: #747d8d; font-size: 10px; line-height: 1.7; }
.token-error-table th:first-child { width: 105px; }
.token-error-table th:nth-child(2) { width: 190px; }
.response-code-heading { margin-top: 32px; }
.response-field-table th:first-child { width: 130px; }
.response-field-table th:nth-child(2) { width: 185px; }
.response-field-table td { line-height: 1.7; }
.response-code-table th:first-child { width: 130px; }
.field-list { margin-top: 16px; border-top: 1px solid var(--line); }
.field-list div { display: grid; grid-template-columns: 140px 1fr; gap: 20px; padding: 14px 4px; border-bottom: 1px solid var(--line); color: #70798a; font-size: 11px; line-height: 1.7; }
.field-list code { color: #a25f00; font-weight: 650; }
.response-fields { margin-top: 0; }
.info-note { display: flex; gap: 12px; margin-top: 17px; padding: 15px 17px; color: #45617c; border: 1px solid #cfe1f2; border-radius: 9px; background: #f1f7fd; }
.info-note svg { width: 19px; height: 19px; flex: 0 0 19px; fill: none; stroke: #4b78a4; stroke-width: 1.7; stroke-linecap: round; }
.info-note p { margin: 0; font-size: 11px; line-height: 1.75; }
.status-code { display: inline-block; min-width: 34px; padding: 3px 6px; border-radius: 4px; text-align: center; font-size: 10px; font-weight: 750; }
.status-code.warn { color: #9a6108; background: #fff0cc; }
.status-code.danger { color: #a63842; background: #fdecee; }
.status-code.neutral { color: #5f6877; background: #eceef2; }
.practice-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
.practice-grid > div { position: relative; min-height: 145px; padding: 23px; overflow: hidden; border: 1px solid var(--line); border-radius: 11px; background: #fff; }
.practice-grid strong { position: relative; display: block; margin-bottom: 10px; color: #333c4d; font-size: 14px; }
.practice-grid p { position: relative; max-width: 85%; margin: 0; color: #7a8393; font-size: 11px; line-height: 1.8; }

.docs-footer { display: flex; justify-content: flex-end; width: min(1060px, calc(100% - 80px)); margin: 0 auto; padding: 22px 0 30px; color: #a3a9b3; border-top: 1px solid var(--line); font-size: 10px; }
.copy-toast { position: fixed; z-index: 50; left: 50%; bottom: 28px; padding: 10px 16px; color: #fff; border-radius: 8px; background: #273142; box-shadow: 0 10px 28px rgba(22, 30, 48, .22); font-size: 12px; transform: translateX(-50%); }
.toast-enter-active, .toast-leave-active { transition: opacity .2s ease, transform .2s ease; }
.toast-enter, .toast-leave-to { opacity: 0; transform: translate(-50%, 8px); }

@media (max-width: 1050px) {
  .docs-sidebar { width: 238px; flex-basis: 238px; }
  .docs-article, .docs-footer { width: calc(100% - 48px); }
  .hero-section { grid-template-columns: 1fr; }
  .hero-visual { display: none; }
  .auth-flow { grid-template-columns: 1fr 70px 1fr 70px 1fr; padding: 18px; }
}

@media (max-width: 760px) {
  .docs-header { height: 60px; padding: 0 16px; }
  .brand-name { font-size: 15px; }
  .brand-divider { display: none; }
  .brand-product { font-size: 12px; }
  .docs-layout { height: calc(100% - 60px); flex-direction: column; }
  .docs-sidebar { width: 100%; flex: 0 0 auto; border-right: 0; border-bottom: 1px solid var(--line); }
  .sidebar-inner { padding: 10px 14px; }
  .nav-caption { display: none; }
  .docs-nav { flex-direction: row; }
  .nav-item { grid-template-columns: 30px 1fr; padding: 8px 10px; }
  .nav-icon { width: 30px; height: 30px; }
  .nav-icon svg { width: 17px; height: 17px; }
  .nav-item small, .nav-arrow { display: none; }
  .nav-item strong { margin: 0; font-size: 12px; }
  .api-nav-caption { display: none; }
  .api-catalog-root { margin-top: 6px; }
  .api-catalog-root small { display: none; }
  .api-catalog-tree { max-height: 190px; overflow-y: auto; }
  .docs-article, .docs-footer { width: calc(100% - 32px); }
  .docs-article { padding-top: 20px; }
  .breadcrumb { margin-bottom: 22px; }
  .hero-section { min-height: auto; padding: 30px 24px; }
  .hero-copy h1, .article-title h1 { font-size: 32px; letter-spacing: -1px; }
  .capability-grid, .intro-guide-grid, .practice-grid, .spec-grid, .response-example-grid { grid-template-columns: 1fr; }
  .spec-grid div, .spec-grid div:nth-child(odd) { border-right: 0; border-bottom: 1px solid var(--line); }
  .spec-grid div:last-child { border-bottom: 0; }
  .steps-panel { flex-direction: column; gap: 18px; }
  .step-line { width: 1px; height: 22px; margin: -7px 0 -7px 14px; }
  .auth-flow { grid-template-columns: 1fr; gap: 12px; }
  .flow-node { min-height: 130px; }
  .flow-arrow { transform: rotate(90deg); }
  .flow-arrow span { display: none; }
  .api-table-wrap, .error-table-wrap { overflow-x: auto; }
  .api-table, .error-table { min-width: 650px; }
  .field-list div { grid-template-columns: 1fr; gap: 5px; }
  .content-section { padding-top: 52px; }
  .section-index { min-width: 38px; font-size: 17px; }
}
</style>
